import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { toast } from "sonner";
import { paymentEndpoint, childEndpoints } from "@/config/endpoints";
import { invalidateLeadBoard, invalidateRecentActivityFeed } from "@/lib/queryClient";

interface CreatePaymentPayload {
  childId: number;
  amount: number;
  method: string;
  txnId?: string;
  note?: string;
  paidAt?: string;
  receiptDocId?: number;
}

interface UpdatePaymentPayload {
  amount?: number;
  method?: string;
  txnId?: string;
  note?: string;
  paidAt?: string;
  receiptDocId?: number;
}

/** Exported for the coupon apply / remove mutations (features/coupons). */
export function useInvalidatePaymentQueries() {
  const queryClient = useQueryClient();
  return () => {
    // A payment recomputes the child's paymentStatus, which rolls up into the
    // lead-card payment chip (`aggregatePaymentStatus`, rendered by
    // PaymentStatusBadge on every board/grid/dynamic card when the
    // isPaymentStatusShown toggle is on) — so it DOES touch a board field.
    // invalidateLeadBoard() (active-only) refreshes that chip; the rest cover
    // the deal widgets + timeline. We still drop `refetchType:"all"` so only
    // mounted observers refetch.
    invalidateLeadBoard();
    queryClient.invalidateQueries({ queryKey: ["childs"] });
    queryClient.invalidateQueries({ queryKey: ["get-child"] });
    queryClient.invalidateQueries({ queryKey: ["activity-log"] });
    invalidateRecentActivityFeed();
    queryClient.invalidateQueries({ queryKey: ["payments"] });
    queryClient.invalidateQueries({ queryKey: ["payment-summary"] });
  };
}

export function useCreatePayment() {
  const invalidate = useInvalidatePaymentQueries();

  return useMutation({
    mutationFn: async (data: CreatePaymentPayload) => {
      const response = await axiosInstance.post(paymentEndpoint, data);
      return response.data;
    },
    onSuccess: () => {
      toast.success("Payment recorded successfully");
      invalidate();
    },
    onError: (error: any) => {
      const msg = error.response?.data?.message || "Failed to record payment";
      toast.error(msg);
    },
  });
}

export function useUpdatePayment() {
  const invalidate = useInvalidatePaymentQueries();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: UpdatePaymentPayload;
    }) => {
      const response = await axiosInstance.patch(
        `${paymentEndpoint}/${id}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success("Payment updated");
      invalidate();
    },
    onError: (error: any) => {
      const msg = error.response?.data?.message || "Failed to update payment";
      toast.error(msg);
    },
  });
}

export function useDeletePayment() {
  const invalidate = useInvalidatePaymentQueries();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await axiosInstance.delete(`${paymentEndpoint}/${id}`);
      return response.data;
    },
    onSuccess: () => {
      toast.success("Payment deleted");
      invalidate();
    },
    onError: (error: any) => {
      const msg = error.response?.data?.message || "Failed to delete payment";
      toast.error(msg);
    },
  });
}

export function usePaymentsByChild(childId: number | null) {
  return useQuery({
    queryKey: ["payments", "child", childId],
    enabled: !!childId,
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${paymentEndpoint}/child/${childId}`
      );
      return response.data;
    },
  });
}

export function usePaymentsByLead(leadId: number | null) {
  return useQuery({
    queryKey: ["payments", "lead", leadId],
    enabled: !!leadId,
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${paymentEndpoint}/lead/${leadId}`
      );
      return response.data;
    },
  });
}

export function useLeadPaymentSummary(leadId: number | null) {
  return useQuery({
    queryKey: ["payment-summary", leadId],
    enabled: !!leadId,
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${paymentEndpoint}/lead/${leadId}/summary`
      );
      return response.data;
    },
  });
}

export function useAttachVariant() {
  const invalidate = useInvalidatePaymentQueries();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      childId,
      moduleScopes,
      financials,
      customInputScopes,
      name,
    }: {
      childId: number;
      moduleScopes: { nicheModelName: string; nicheItemId: number }[];
      financials: {
        totalDealValue: number;
        netAmount: number;
        discountAmount?: number;
      };
      customInputScopes?: {
        customInputId?: number | string;
        optionIds?: string[];
        textValue?: string;
      }[];
      /** Rename the deal with the attach (a variant change follows its name). */
      name?: string;
      /** Toast on success; defaults to the attach wording. */
      successMessage?: string;
    }) => {
      const response = await axiosInstance.patch(
        `${childEndpoints}/${childId}`,
        {
          moduleScopes,
          financials,
          ...(customInputScopes ? { customInputScopes } : {}),
          ...(name ? { name } : {}),
        }
      );
      return response.data;
    },
    onSuccess: (_data, vars) => {
      toast.success(vars.successMessage ?? "Service variant attached successfully");
      invalidate();
      // The attach renames/relinks the deal: the lead payload's `child[]` and
      // the card deal-hover tooltip render it too. (Callers no longer refetch
      // these themselves on close.)
      queryClient.invalidateQueries({ queryKey: ["get-leads"] });
      queryClient.invalidateQueries({ queryKey: ["lead-children-hover"] });
    },
    onError: (error: any) => {
      const msg =
        error.response?.data?.message || "Failed to attach service variant";
      toast.error(msg);
    },
  });
}
